import React, {useEffect, useState} from 'react';
import Page from "../Components/Page";
import {Breadcrumbs} from "../Components/Breadcrumbs";
import Checkbox from "../Components/Checkbox";
import _ from "lodash";
import SelectDropdown from "../Components/Dropdowns/SelectDropdown";
import {makeApolloClientCall} from "../../Api/makeApolloApiCall";
import {makeApiCall} from "../../Api/makeApiCall";
import {currentWeekIdQuery} from "../SldRelease/SldReleasePage";
import {LoadingWrapper} from "../Components/LoadingWrapper";
import AirflowDagLogs from "../Components/airflow/dagLogs/AirflowDagLogs";
import {toastError, toastSuccess} from "../../utils/toast";
import {toast} from "react-toastify";
import {getUserEmail} from "../../auth/accessTokenValidator";
import {invokeDAGviaApiPost} from "../../Api/invokeDag";
import {
    buildCleanseFillExtractCheckpointOptions,
    buildPeriodCheckpointMap,
    CLEANSE_FILL_EXTRACT_SCHEMA_NAME,
    getCheckpointListDirectoriesUrl,
    getDefaultPeriodCheckpointsUrl,
    resolveCheckpointRunIdentifierForSubmit,
} from "../../utils/cleanseFillExtractCheckpoints";
import {wrapValueInOptionStructure} from "../../utils/maybeAddLabelToOptions";
import {AVAILABLE_PROJECTION_VERSIONS_QUERY} from "./Components/VersionSelect";


const PROJECTIONS_EXTRACTS_NAMES = [
    {label: 'weekly_acvs_by_source_record', tableName: 'weekly_acvs_by_source_record'},
    {label: 'store_universe', tableName: 'store_universe'},
    {label: 'store_universe_summary', tableName: 'store_universe_summary'},
    {label: 'projection_factors', tableName: 'projection_factors'},
]

const DAG_NAME = "edp_projection_qc_extracts";

const ProjectionsExtract = () => {

    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState(false);

    const [bigQueryPrefix, setBigQueryPrefix] = useState(undefined);
    const [selectedMarketPeriod, setSelectedMarketPeriod] = useState(undefined);
    const [checkedTables, setCheckedTables] = useState(PROJECTIONS_EXTRACTS_NAMES.map(table => table.tableName));
    const [selectedProjectionVersion, setSelectedProjectionVersion] = useState(undefined);

    const [marketPeriodOptions, setMarketPeriodOptions] = useState([]);
    const [checkpointOptions, setCheckpointOptions] = useState([]);
    const [periodCheckpointMap, setPeriodCheckpointMap] = useState({});
    const [selectedCheckpoint, setSelectedCheckpoint] = useState(undefined);
    const [availableProjectionVersionsOptions, setAvailableProjectionVersionsOptions] = useState([]);
    const [dagLogsRefreshTrigger, setDagLogsRefreshTrigger] = useState(undefined);

    useEffect(() => {
        setLoading(true);
        setLoadError(false);
        Promise.all([
            makeApolloClientCall(currentWeekIdQuery, true),
            makeApolloClientCall('{ marketPeriodList }'),
            makeApiCall(getCheckpointListDirectoriesUrl()),
            makeApiCall(getDefaultPeriodCheckpointsUrl()),
            makeApolloClientCall(AVAILABLE_PROJECTION_VERSIONS_QUERY),

        ])
            .then(([
                       currentWeekIdQueryResults,
                       marketPeriodResults,
                       cleanseFillExtractBuckets,
                       periodCheckpointsResult,
                       availableProjectionVersions,
                   ]) => {

                const currentPeriodAbbreviation = currentWeekIdQueryResults.currentPeriodQuad?.[0]?.periodAbbreviation;
                const checkpointOpts = buildCleanseFillExtractCheckpointOptions(cleanseFillExtractBuckets);

                setSelectedMarketPeriod(currentPeriodAbbreviation);
                setCheckpointOptions(checkpointOpts);
                setPeriodCheckpointMap(buildPeriodCheckpointMap(periodCheckpointsResult));

                const marketPeriodOptions = (marketPeriodResults?.marketPeriodList || []).slice().sort().reverse().map(mp => wrapValueInOptionStructure(mp));
                setMarketPeriodOptions(marketPeriodOptions);
                setAvailableProjectionVersionsOptions(availableProjectionVersions?.availableProjectionVersions?.map(v => wrapValueInOptionStructure(v.version) || []));
            })
            .catch(() => setLoadError(true))
            .finally(() => setLoading(false));
    }, []);

    const onPeriodChange = (option) => {
        setSelectedMarketPeriod(option.value);
        setSelectedCheckpoint(undefined);
    };

    const onSubmitButtonClicked = () => {
        const runIdentifier = resolveCheckpointRunIdentifierForSubmit(selectedCheckpoint, periodCheckpointMap, checkpointOptions);

        const data = {
            period: selectedMarketPeriod,
            spins_store_sales_checkpoint: {
                schema_name: CLEANSE_FILL_EXTRACT_SCHEMA_NAME,
                run_identifier: runIdentifier,
            },
            extract_bigquery_table_prefix: bigQueryPrefix?.trim() || undefined,
            extract_tables_to_include: checkedTables,
            triggered_by: getUserEmail(),
            version: selectedProjectionVersion
        }

        toastSuccess(toast, `${DAG_NAME} DAG config ${JSON.stringify(data, null, 2)}`)
        invokeDAGviaApiPost(DAG_NAME, data)
            .then((response) => {
                if (response?.apiError)
                    toastError(toast, `${DAG_NAME} DAG trigger failed with error ${JSON.stringify(response.apiError)}`)
                else
                    toastSuccess(toast, `${DAG_NAME} DAG triggered successfully`)
            })
            .catch(e => {
                toastError(toast, `${DAG_NAME} DAG trigger failed with error ${JSON.stringify(e)}`)
            })
            .finally(() => {
                setDagLogsRefreshTrigger(Date.now())
            });
    }

    const canSubmit = !loadError && selectedProjectionVersion && selectedMarketPeriod && selectedCheckpoint;

    return <Page breadCrumbs={<Breadcrumbs items={[{label: "Services"}, {label: "Projections"}, {label: "Projection Extract"}]}/>} name={"Projections Extract"} beta={true}>
        <LoadingWrapper loading={loading}>
            {loadError && <div className={'error-message'} data-testid={'load-error-message'}>
                There was an error loading checkpoint data. Please try again later.
            </div>}
            <h5>Configuration Options</h5>
            <div className={"projection-extract-container"}>
                <div className={"custom-run-selections"}>
                    <div className={"custom-run-selections-column"}>
                        <div id={'required_column_header'} className={"custom-run-selections-row custom-run-section-header"}>
                            <h6>Required</h6>
                        </div>
                        <div id={'market_period_dropdown_div'} className={"custom-run-selections-row"}>
                            <SelectDropdown id={"market_period_dropdown"}
                                            dataTestId={"market_period"}
                                            inputLabel={"Period"}
                                            selectValue={selectedMarketPeriod}
                                            options={marketPeriodOptions}
                                            onChange={onPeriodChange}
                            />
                        </div>
                        <div id={'projection_version_dropdown_div'}
                             className={"custom-run-selections-row"}>
                            <SelectDropdown id={"projection_version_dropdown"}
                                            className={"optional-dropdown"}
                                            dataTestId={"projection_version"}
                                            inputLabel={"Projection Version"}
                                            selectValue={selectedProjectionVersion}
                                            options={availableProjectionVersionsOptions}
                                            onChange={(option) => setSelectedProjectionVersion(option?.value)}
                            />
                        </div>
                        <div id={'spins_store_sales_checkpoint_dropdown_div'}
                             className={"custom-run-selections-row"}>
                            <SelectDropdown id={"spins_store_sales_checkpoint_dropdown"}
                                            className={"optional-dropdown"}
                                            dataTestId={"spins_store_sales_checkpoint"}
                                            inputLabel={"SPINS Store Sales Checkpoint"}
                                            selectValue={selectedCheckpoint}
                                            options={checkpointOptions}
                                            onChange={(option) => setSelectedCheckpoint(option?.value)}
                            />
                        </div>
                        <div id={'tables_checkboxes_div'} className={"custom-run-selections-row"}>
                            <div className={"tables-checkboxes-container input-group"}>
                                <span className='input-group-label'>Tables to Create:</span>
                                <div className={'tables-checkboxes'}>
                                    {PROJECTIONS_EXTRACTS_NAMES.map(({label, tableName}) => (
                                        <div key={`checkbox-wrapper-${tableName}`}
                                             style={{display: 'flex', alignItems: 'center'}}>
                                            <Checkbox
                                                key={`checkbox-${tableName}`}
                                                labelText={label}
                                                checked={checkedTables.includes(tableName)}
                                                onChange={() => {
                                                    if (checkedTables.includes(tableName)) {
                                                        setCheckedTables(_.without(checkedTables, tableName));
                                                    } else {
                                                        setCheckedTables([...checkedTables, tableName]);
                                                    }
                                                }}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className={"custom-run-selections"}>
                    <div className={"custom-run-selections-column"}>
                        <div id={'optional_column_header'} className={"custom-run-selections-row custom-run-section-header"}>
                            <h6>Optional</h6>
                        </div>
                        <div id={'big_query_prefix_div'} className={"custom-run-selections-row projection-extract-optional-field"}>
                            <div className={'input-group'}>
                                <span className='input-group-label'>{'Big Query Prefix'}:</span>
                                <input
                                    id={'alternate-source-identifier-input'}
                                    data-testid={"big_query_prefix__input"}
                                    className={'input-group-field'}
                                    type='text'
                                    value={bigQueryPrefix || ''}
                                    onChange={(e) => setBigQueryPrefix(e.target.value)}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className={"trigger-dag-submit-button-container"}>
                <button type="button"
                        className="button primary measures-trigger-submit"
                        onClick={onSubmitButtonClicked}
                        disabled={!canSubmit}
                >
                    Submit
                </button>
            </div>
        </LoadingWrapper>
        <div className="sld-dag-airflowlogs-container" data-testid="airflow-logs-container">
            <h5>{DAG_NAME} Run Log</h5>
            <AirflowDagLogs
                processName={DAG_NAME}
                includeVersion={true}
                setAutoRefresh={true}
                refreshTrigger={dagLogsRefreshTrigger}
            />
        </div>

    </Page>;
};

export default ProjectionsExtract;
